import { useEffect, useMemo, useState, useRef } from 'react';
import RepeaterEditor from '../components/RepeaterEditor';
import ImageUploader from '../components/ImageUploader';
import { useAdminApi } from '../../../lib/adminApi';

const defaults = {
  sectionId: "Galerie",
  title: "Festival-Galerie",
  images: []
};

// turn old strings into objects
const normalizeImages = (images = []) =>
  images.map(it => (typeof it === 'string' ? { url: it, year: '' } : { url: it.url || '', year: it.year ?? '' }));

export default function GalleryEditor(){
  const api = useAdminApi();

  const bulkRef = useRef(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkYear, setBulkYear] = useState(''); // default year for bulk uploads

  const [doc, setDoc] = useState(defaults);
  const [orig, setOrig] = useState(defaults);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const dirty = useMemo(()=>JSON.stringify(doc)!==JSON.stringify(orig), [doc,orig]);

  useEffect(()=>{ let alive=true;(async()=>{
    setLoading(true);
    try {
      const res = await api.get('/sections/gallery'); if(!alive) return;
      const merged = { ...defaults, ...(res||{}) };
      const normalized = { ...merged, images: normalizeImages(merged.images) };
      setDoc(normalized); setOrig(normalized);
    } finally { setLoading(false); }
  })(); return ()=>{alive=false}; },[]); // eslint-disable-line

  const onSave = async() => { setSaving(true); setError('');
    try{
      // doc already has objects { url, year }
      await api.put('/sections/gallery', doc);
      setOrig(doc);
      setEditing(false);
    }catch(e){ setError(e?.message || 'Save failed'); }
    finally{ setSaving(false); }
  };

  if (loading) return <Spinner/>;
  const readOnly = !editing;

  return (
    <div>
      <Toolbar
        title="Festival-Galerie"
        editing={editing}
        setEditing={setEditing}
        onSave={onSave}
        onCancel={()=>{setDoc(orig); setEditing(false);}}
        saving={saving}
        dirty={dirty}
      />
      {error && <div className="alert alert-danger m-3 py-2">{error}</div>}

      <div className="p-3">
        <Row>
          <Col>
            <Text label="Titel" value={doc.title} onChange={(v)=>setDoc(d=>({...d, title:v}))} readOnly={readOnly}/>
          </Col>
        </Row>

        {/* Bulk upload */}
        {editing && (
          <div className="mb-3">
            <label className="form-label">Mehrere Bilder hochladen</label>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: 160 }}
                placeholder="Jahr (z.B. 2023)"
                value={bulkYear}
                onChange={(e)=>setBulkYear(e.target.value)}
              />
              <button type="button" className="btn btn-edit btn-sm" onClick={()=>bulkRef.current?.click()}>
                Dateien auswählen…
              </button>
              {bulkUploading && <span className="small text-muted">Lädt hoch… {bulkProgress}%</span>}
            </div>
            <input
              ref={bulkRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={async (e)=>{
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                setBulkUploading(true); setBulkProgress(0);
                const newItems = [];
                let done = 0;
                for (const f of files) {
                  try {
                    const url = await api.upload(f, {
                      path: 'gallery',
                      onProgress: (p)=>setBulkProgress(Math.round((done*100 + p)/files.length))
                    });
                    newItems.push({ url, year: bulkYear || '' });
                  } catch {
                    // skip failed upload
                  }
                  done++;
                }
                setDoc(d => ({ ...d, images: [...(d.images||[]), ...newItems] }));
                setBulkUploading(false); setBulkProgress(0);
                e.target.value = null;
              }}
            />
            <div className="form-text">
              JPG/PNG, max 8 MB je Datei. Das optionale Feld <b>Jahr</b> wird für alle hochgeladenen Bilder übernommen.
            </div>
          </div>
        )}

        <RepeaterEditor
          title="Bilder"
          items={doc.images || []}                        // now objects: { url, year }
          onChange={(v)=>setDoc(d=>({...d, images: v}))} // keep objects when saving
          template={{ url:'', year:'' }}
          editing={editing}     
          renderFields={(it, patch)=>(
              <div className="row g-2 align-items-start admin-gallery-row">
                {/* Uploader */}
                <div className="col-8 col-md-6">
                  <ImageUploader
                    label="Bild"
                    value={it.url}
                    onChange={(url)=>patch({ url })}
                    folder="gallery"
                    readOnly={readOnly}
                    help="Klick/Drag&Drop zum Ersetzen."
                  />
                </div>

                {/* Year */}
                <div className="col-12 col-md-3">
                  <label className="form-label">Jahr</label>
                  <input
                    type="text"
                    className="form-control"
                    value={it.year ?? ''}
                    onChange={(e)=>patch({ year: e.target.value })}
                    readOnly={readOnly}
                    placeholder="z.B. 2023"
                  />
                </div>
              </div>
            )}
        />
      </div>
    </div>
  );
}

function Toolbar(p){ return CarouselToolbar(p); }
function Spinner(){ return (<div className="p-4 text-center"><div className="spinner-border brand-spinner" role="status" /><div className="small mt-2 text-muted">Lädt…</div></div>); }
const CarouselToolbar = ({ title, editing, setEditing, onSave, onCancel, saving, dirty }) => (
  <div className="editor-toolbar">
    <div className="d-flex align-items-center gap-2">
      <h4 className="editor-title m-0">{title}</h4>
      {editing && dirty && <span className="badge-soft">• Ungespeicherte Änderungen</span>}
    </div>
    <div className="d-flex align-items-center gap-2">
      {!editing ? <button className="btn btn-sm btn-edit" onClick={()=>setEditing(true)}>Bearbeiten</button> : (
        <>
          <button className="btn btn-sm btn-save" onClick={onSave} disabled={!dirty || saving}>{saving ? 'Speichert…' : 'Speichern'}</button>
          <button className="btn btn-sm btn-cancel" onClick={onCancel} disabled={saving}>Abbrechen</button>
        </>
      )}
    </div>
  </div>
);
function Text({label, value, onChange, readOnly}){ return (<div className="mb-3"><label className="form-label">{label}</label><input className="form-control" value={value||''} onChange={e=>onChange(e.target.value)} readOnly={readOnly}/></div>); }
const Row = ({children}) => <div className="row">{children}</div>;
const Col = ({children}) => <div className="col-md-6">{children}</div>;
